import { useCallback, useEffect, useState } from "react";
import { Panel, Btn } from "@/components/admin/AdminShell";
import { api } from "@/lib/api";

const OPTIONS = [
  {
    id: "titan",
    title: "Titan Email",
    desc: "smtp.titan.email, port 465 (SSL). Mailbox address and its Titan password.",
    env: "TITAN_SMTP_USER and TITAN_SMTP_PASSWORD",
  },
  {
    id: "google",
    title: "Google",
    desc: "smtp.gmail.com, port 587 (STARTTLS). Use a Gmail address and an App Password, not the normal password.",
    env: "GOOGLE_SMTP_USER and GOOGLE_SMTP_PASSWORD",
  },
  {
    id: "env",
    title: "Follow server env",
    desc: "Use SMTP_PROVIDER in the backend environment. If that is unset and only one mailbox is configured, that one is used.",
  },
];

function errText(e, fallback) {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
}

export default function EmailSmtpPanel() {
  const [state, setState] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    setLoadErr("");
    try {
      const { data } = await api.get("/api/admin/email-smtp");
      setState(data);
    } catch (e) {
      setLoadErr(errText(e, "Could not load email SMTP settings."));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function choose(opt) {
    if (!state || saving || state.provider === opt.id) return;
    setSaving(true);
    setMsg(null);
    try {
      await api.put("/api/admin/email-smtp", { provider: opt.id });
      setMsg({ ok: true, text: `Email mailbox set to ${opt.title}.` });
      await load();
    } catch (e) {
      setMsg({ ok: false, text: errText(e, "Failed to change mailbox.") });
    } finally {
      setSaving(false);
    }
  }

  async function sendTest() {
    setTesting(true);
    setMsg(null);
    try {
      const { data } = await api.post("/api/admin/email-smtp/test", {});
      setMsg({
        ok: true,
        text: `Test email sent to ${data.to} via ${data.provider}.`,
        spam: true,
      });
    } catch (e) {
      setMsg({ ok: false, text: errText(e, "Test email failed.") });
    } finally {
      setTesting(false);
    }
  }

  if (loadErr) {
    return <Panel className="p-5 max-w-[640px] text-[13px] text-red-400">{loadErr}</Panel>;
  }
  if (!state) {
    return <Panel className="p-5 max-w-[640px] text-[13px] text-muted-foreground">Loading…</Panel>;
  }

  const byId = Object.fromEntries((state.providers || []).map((p) => [p.id, p]));
  const activeLabel = state.effective
    ? (byId[state.effective]?.label || state.effective)
    : "None";

  return (
    <div className="space-y-4 max-w-[640px]" data-testid="email-smtp-settings">
      <Panel className="p-5">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-[14px] font-semibold">Email mailbox</h3>
          <span
            className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${
              state.ready ? "border-line text-muted-foreground" : "border-yellow-500 text-yellow-400"
            }`}
            data-testid="email-smtp-effective"
          >
            Active: {activeLabel}
          </span>
        </div>
        <p className="text-[12px] text-muted-foreground mb-4">
          Signup, login, and password-reset email goes through Titan or Google.
          Passwords stay in the server environment and are never shown here.
        </p>

        {!state.ready && (
          <div className="p-3 mb-4 rounded-md border border-yellow-500/60 bg-yellow-500/10 text-[12px] text-yellow-300">
            No mailbox is ready. Email codes stay on screen (often 123456) until Titan or Google is configured.
          </div>
        )}

        <div className="space-y-2">
          {OPTIONS.map((opt) => {
            const info = byId[opt.id];
            const selected = state.provider === opt.id;
            const blocked = info && !info.configured;
            return (
              <button
                key={opt.id}
                type="button"
                disabled={saving || blocked}
                onClick={() => choose(opt)}
                data-testid={`email-smtp-${opt.id}`}
                className={`w-full text-left p-3 rounded-md border transition ${
                  selected ? "border-primary bg-primary/10" : "border-line bg-background hover:border-primary/60"
                } ${blocked ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <div className="flex items-center gap-2">
                  <span
                    className={`w-3.5 h-3.5 rounded-full border ${
                      selected ? "border-primary bg-primary" : "border-line"
                    }`}
                  />
                  <span className="text-[13px] font-semibold">{opt.title}</span>
                  {info && (
                    <span className={`text-[11px] ml-auto ${info.configured ? "text-green-400" : "text-yellow-400"}`}>
                      {info.configured ? "Configured" : "Not configured"}
                    </span>
                  )}
                </div>
                <div className="text-[12px] text-muted-foreground mt-1 ml-[22px]">{opt.desc}</div>
                {info?.configured && info.from && (
                  <div className="text-[11px] text-muted-foreground mt-1 ml-[22px]">
                    From {info.from} · {info.host}:{info.port}{info.ssl ? " SSL" : " STARTTLS"}
                  </div>
                )}
                {blocked && (
                  <div className="text-[11px] text-yellow-400 mt-1 ml-[22px]">
                    Set {opt.env} on the server, then restart the API.
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {state.provider === "env" && (
          <div className="mt-3 text-[11px] text-muted-foreground">
            Server env selects: {state.env_provider === "env" ? "nothing yet" : state.env_provider}
            {state.custom?.configured ? ` (custom host ${state.custom.host} is also set)` : ""}.
          </div>
        )}

        <div className="mt-4">
          <Btn onClick={sendTest} disabled={testing || !state.ready} data-testid="email-smtp-test">
            {testing ? "Sending…" : "Send test email to me"}
          </Btn>
        </div>

        {msg && (
          <div className={`mt-3 text-[12px] ${msg.ok ? "text-green-400" : "text-red-400"}`} data-testid="email-smtp-msg">
            {msg.text}
            {msg.spam ? <> Also check your <strong>spam folder</strong>.</> : null}
          </div>
        )}
      </Panel>
    </div>
  );
}
